import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { termsAndConditionsAgreedKey } from "common/config";

import { ONBOARDING_INTRO_TOUR } from "../constants";
import {
  onboardingV2IntroOpenAtom,
  onboardingV2IntroReplayAtom,
  onboardingV2IntroStageAtom,
  onboardingV2IntroTourPageAtom,
} from "./atoms";

/**
 * The intro before the questions (`16204:290230`): the welcome, the choice
 * between exploring and the tour, and the tour itself.
 *
 * Both ways out of it end in the same place - the docked questions sheet,
 * which `onboardingV2PresentationAtom` keeps back until the stage is `done`.
 */
export function useOnboardingIntro() {
  const isOpen = useAtomValue(onboardingV2IntroOpenAtom);
  const [stage, setStage] = useAtom(onboardingV2IntroStageAtom);
  const [tourPage, setTourPage] = useAtom(onboardingV2IntroTourPageAtom);
  const [isReplay, setReplay] = useAtom(onboardingV2IntroReplayAtom);

  /**
   * "Agree and continue". Records the agreement the same way the auth modal's
   * terms screen does (`VerifyTermsAndConditions`), so a visitor who agreed
   * here is not asked again when an action needs them to have agreed.
   */
  const acceptTerms = useCallback(() => {
    localStorage.setItem(
      termsAndConditionsAgreedKey,
      JSON.stringify({ accepted: true, timestamp: Date.now() }),
    );
    setStage("choice");
  }, [setStage]);

  /** "I'll figure it out on my own", and "Skip" / "Got it" in the tour. */
  const finish = useCallback(() => {
    setTourPage(null);
    // A replay only closes: the stage is already `done`, and moving it would
    // re-run the intro's own bookkeeping for nothing.
    if (isReplay) {
      setReplay(false);
      return;
    }
    setStage("done");
  }, [isReplay, setReplay, setStage, setTourPage]);

  /** "Show me how it works". */
  const startTour = useCallback(() => setTourPage(0), [setTourPage]);

  const goNext = useCallback(() => {
    if (tourPage === null) return;
    if (tourPage >= ONBOARDING_INTRO_TOUR.length - 1) {
      finish();
      return;
    }
    setTourPage(tourPage + 1);
  }, [finish, setTourPage, tourPage]);

  /**
   * "Previous". On the first page it goes back to "Let's get started!", the
   * screen the tour was opened from, rather than doing nothing.
   */
  const goPrevious = useCallback(() => {
    if (tourPage === null) return;
    // A replay was not opened from "Let's get started!", so there is nothing
    // before its first page to go back to.
    if (tourPage === 0 && isReplay) {
      finish();
      return;
    }
    setTourPage(tourPage === 0 ? null : tourPage - 1);
  }, [finish, isReplay, setTourPage, tourPage]);

  return useMemo(
    () => ({
      isOpen,
      isReplay,
      stage,
      tourPage,
      acceptTerms,
      finish,
      startTour,
      goNext,
      goPrevious,
    }),
    [
      acceptTerms,
      finish,
      goNext,
      goPrevious,
      isOpen,
      isReplay,
      stage,
      startTour,
      tourPage,
    ],
  );
}

/**
 * Opens the intro's "how it works" tour on its own first page — the menu's
 * "How the app works", which used to open the legacy CMS carousel.
 */
export function useReplayOnboardingIntroTour() {
  const setTourPage = useSetAtom(onboardingV2IntroTourPageAtom);
  const setReplay = useSetAtom(onboardingV2IntroReplayAtom);
  return useCallback(() => {
    setTourPage(0);
    setReplay(true);
  }, [setReplay, setTourPage]);
}
